import React from 'react';
import { speakSegment } from '../speakSegment';
import { IpaList } from './IpaList';
import { IpaType } from '../../phonemes/ipa';

/**
 * Show various sets of IPA phonemes,
 * bolding the ones supported by eSpeak's English phoneme set
 */
export function ShowPhonemes(props: { clicked?: (ipa: IpaType) => void }) {
  function clicked(ipa: IpaType): void {
    toClipboard(ipa);
    if (props.clicked) {
      props.clicked(ipa);
    }
  }

  return (<>
    <Misc clicked={clicked} />
    <br /><br />
    <Vowels clicked={clicked} />
    <br />
    <RColored clicked={clicked} />
    <Diphthongs clicked={clicked} />
    <Syllabic clicked={clicked} />
    <br />
    <Consonants clicked={clicked} />
  </>);
}


function Misc(props: { clicked: (ipa: IpaType) => void }) {
  const misc = 'ˈ , ˌ , ː , ˑ , ə̆ , \u0306';
  const tones = '˥ ˦ ˧ ˨ ˩ ˦˨ ˨˦ ˥˩ ˩˥ ˨˦˨ ˦˨˦';
  return (<>
    <IpaList clicked={props.clicked} bolded={misc} items={misc} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={tones} items={'˥ ˦ ˧ ˨ ˩ , ˦˨ ˨˦ , ˥˩ ˩˥ , ˨˦˨ ˦˨˦'} />
  </>);
}

function Vowels(props: { clicked?: (ipa: IpaType) => void }) {
  const all = 'i y ɨ ʉ ɯ u ɪ ʏ ʊ e ø ɘ ɵ ɤ o e̞ ø̞ ə ɤ̞ o̞ ɛ œ ɜ ɞ ʌ ɔ æ ɐ a ɶ ɑ ɒ ĩ ũ ɪ̃ ẽ õ ɛ̃ ʌ̃ ɔ̃ æ̃ ɐ̃ ə̃';
  return (<>
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'i y , ɨ ʉ , ɯ u'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'ɪ ʏ , ʊ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'e ø , ɘ ɵ , ɤ o'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'e̞ ø̞ , ə , ɤ̞ o̞'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'ɛ œ , ɜ ɞ , ʌ ɔ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'æ , ɐ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'a ɶ , ɑ ɒ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={all} items={'ĩ ũ , ɪ̃ , ẽ õ , ə̃ , ɛ̃ ʌ̃ ɔ̃ , æ̃ ɐ̃ , ̃  '} /><br />
  </>
  );
}

function RColored(props: { clicked: (ipa: IpaType) => void }) {
  const rcolored = 'ɚ ɝ ɑɹ e̞ɹ ɪɹ oɹ ɤɹ';
  const supported = rcolored.substring(2);
  return (<>
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={rcolored} />
    <br />
  </>);
}

function Diphthongs(props: { clicked: (ipa: IpaType) => void }) {
  const diphthongsSupported = 'ɛə̯ ɐʉ̯ əʊ̯ ɯu̯';
  const extended = 'aɪ̯ ɔɪ̯ eʊ̯ eɪ̯ ɛɪ̯ ɪə̯ oʊ̯ uə̯';
  const diphthongs = extended.concat(' ' + diphthongsSupported);
  return (<>
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={diphthongsSupported} items={diphthongs} />
    <br />
  </>);
}

/**non-syllabic vowels (e.g., 2nd half of a diphthong) + syllabic consonants */
function Syllabic(props: { clicked: (ipa: IpaType) => void }) {
  const syllabic = 'i̯ u̯ ə̯ ɝ̯  ̯  , l̩ ɫ̩ m̩ n̩ ŋ̩ r̩ ɹ̩ ̩ ';
  const bolded = 'i̯ u̯ ə̯ ɝ̯ l̩ ɫ̩ m̩ n̩ ŋ̩ r̩ ɹ̩';
  return (<>
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={bolded} items={syllabic} />
    <br />
  </>);
}

function Consonants(props: { clicked: (ipa: IpaType) => void }) {
  const basic = 'p b t̪ d̪ t d c ɟ k ɡ q ʔ m n ɳ ɲ ŋ r ʀ ɾ ᴅ ɽ tʃ dʒ tɕ dʑ β f v θ ð w s z ʃ ʒ ʂ ʐ ç ʝ ɕ ʑ x ɣ χ ʁ h ɬ ɮ ʋ ɹ j l ɫ ɭ ʎ ʘ ǀ ǁ ǃ ǂ';
  const supported = basic.concat(' ʈ ɖ r̝ ts dz t͡ʃ d͡ʒ ʈʂ tɕ tɬ dɮ pʼ tʼ cʼ kʼ pʰ tʰ kʰ ʲ pʲ tʲ kʲ');

  return (<>
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'p b t̪ d̪ t d ʈ ɖ c ɟ k ɡ q ɢ ʡ • • ʔ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'• m • ɱ • n̪ • n • ɳ • ɲ • ŋ • ɴ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'• ʙ • r̪ • r • r̝ • ʀ ʜ ʢ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'• ⱱ • ɾ̪ • ɾ • ᴅ • ɽ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'ts dz tʃ dʒ ʈʂ ɖʐ tɕ dʑ tɬ dɮ , t͡ʃ d͡ʒ , ͡ '} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'ɸ β f v θ ð ʍ • s z ʃ ʒ ʂ ʐ ç ʝ ɕ ʑ x ɣ χ ʁ ħ ʕ h ɦ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'ɬ ɮ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'• ʋ • w • ɹ • ɻ • j • ɰ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'• l̪ • l • ɫ • ɭ • ʎ • ʟ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'pʼ • t̪ʼ • tʼ • ʈʼ • cʼ • kʼ • qʼ •'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'ɸʼ • fʼ • θʼ ðʼ • wʼ sʼ zʼ ʃʼ ʒʼ ʂʼ • çʼ • ɕʼ • xʼ • χʼ • • ʕʼ hʼ •'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'pʰ tʰ kʰ ʰ • pʲ tʲ kʲ ʲ • pʷ tʷ kʷ ʷ'} /><br />
    <IpaList clicked={props.clicked} rightClicked={speakSegment} bolded={supported} items={'ʘ • ǀ • ǁ • ǃ • ‼ • ǂ •'} /><br />
  </>
  );
}

export function toClipboard(ipa: string) {
  if (ipa !== '•') {
    navigator.clipboard.writeText(ipa);
  }
}
